import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { billing } from '@repletoryreach/sdk-api';
import { Stack, Group, Text, Button, Loader } from '@repletoryreach/sdk-ui';
import { Modal } from '@repletoryreach/sdk-ui/overlays';
import { Success, Error as ErrorIcon, Clock } from '@repletoryreach/sdk-ui/icons';
import { formatMinor } from '../../../lib/format';
import { useCredits } from '../../../lib/credits';
import { useWallet } from '../../../data/useWallet';
import { usePaymentProviders } from '../../../data/usePaymentProviders';
import { useT } from '../../../i18n/I18nProvider';
import './topup-result.css';

/** Brand names, for when the platform's list of providers cannot be read. */
const BRANDS: Record<string, string> = { payfast: 'PayFast', paystack: 'Paystack', stripe: 'Stripe' };

/** How long "Confirming" shows before the popup says the gateway is taking its time. */
const SLOW_AFTER_MS = 20_000;

type Phase = 'checking' | 'slow' | 'success' | 'failed';

/**
 * The popup a payer sees on coming back from the payment page (21 September 2026).
 *
 * Coming back proves nothing: the gateway sends the payer back and, separately,
 * tells the platform the payment went through, and either can arrive first. So
 * this asks the platform about this one payment (the `topup_id` billing adds to
 * the return address) until the gateway has confirmed it, and only then says it
 * worked, with what was paid, what reached the wallet and the balance now.
 */
export function TopupResultModal({ topupId, onClose, onUnknown }: {
  topupId: string;
  onClose: () => void;
  /** The payment cannot be read (not this organisation's, or the service is down). */
  onUnknown: () => void;
}) {
  const t = useT();
  const nav = useNavigate();
  const qc = useQueryClient();
  const credits = useCredits();
  const walletQ = useWallet();
  const providersQ = usePaymentProviders();
  const headingId = useId();
  const bodyId = useId();
  const started = useRef(Date.now());
  const [slow, setSlow] = useState(false);
  const [confirmedAt, setConfirmedAt] = useState<number | null>(null);

  const topupQ = useQuery({
    queryKey: ['payments', 'topup', topupId],
    queryFn: async () => (await billing.getTopup(topupId)).data,
    retry: 1,
    // Every two seconds while the gateway is expected, then every ten, and
    // after ten minutes the page's own balance is the answer.
    refetchInterval: (query) => {
      if (query.state.data && query.state.data.status !== 'pending') return false;
      const waited = Date.now() - started.current;
      return waited < 90_000 ? 2_000 : waited < 600_000 ? 10_000 : false;
    }
  });
  const topup = topupQ.data;

  useEffect(() => {
    const timer = window.setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, []);

  // Only a payment that could never be read gives up; a poll that fails once
  // the payment is known keeps the popup, and the next poll tries again.
  useEffect(() => {
    if (topupQ.isError && !topupQ.data) onUnknown();
  }, [topupQ.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  // Once confirmed, the balance and the ledger on the page behind are stale.
  useEffect(() => {
    if (topup?.status !== 'completed' || confirmedAt !== null) return;
    setConfirmedAt(Date.now());
    qc.invalidateQueries({ queryKey: ['wallet'] });
    qc.invalidateQueries({ queryKey: ['ledger'] });
    qc.invalidateQueries({ queryKey: ['subscription'] });
    qc.invalidateQueries({ queryKey: ['billing', 'card'] });
  }, [topup?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const phase: Phase =
    topup?.status === 'completed' ? 'success'
      : topup?.status === 'failed' || topup?.status === 'cancelled' ? 'failed'
        : slow ? 'slow' : 'checking';
  const provider = topup
    ? providersQ.data?.find((p) => p.provider === topup.provider)?.name ?? BRANDS[topup.provider] ?? topup.provider
    : '';
  // The balance is shown only once it has been read after the confirmation,
  // so the popup never pairs "successful" with the balance from before.
  const balanceFresh = confirmedAt !== null && walletQ.data != null && walletQ.dataUpdatedAt >= confirmedAt;

  // A payment for a plan, or one that saved a card (11 October 2026), is not a top-up: say what it did.
  const planPayment = topup?.purpose === 'plan_subscribe' || topup?.purpose === 'plan_change';
  const cardSaved = topup?.purpose === 'card_update';
  const heading = {
    checking: t('billing.topup.result.checking.title'),
    slow: t('billing.topup.result.slow.title'),
    success: planPayment ? t('billing.card.result.planTitle') : cardSaved ? t('billing.card.result.cardTitle') : t('billing.topup.result.success.title'),
    failed: t('billing.topup.result.failed.title')
  }[phase];
  const body = !topup
    ? t('billing.topup.result.checking.bodyPlain')
    : {
        checking: t('billing.topup.result.checking.body', { provider }),
        slow: t('billing.topup.result.slow.body', { provider }),
        success: planPayment ? t('billing.card.result.planBody')
          : cardSaved ? t('billing.card.result.cardBody', { credits: credits(topup.credited_minor ?? 0) })
          : t('billing.topup.result.success.body', { credits: credits(topup.credited_minor ?? 0) }),
        failed: t('billing.topup.result.failed.body', { provider })
      }[phase];

  return (
    // No header strip: the heading sits under the icon, so the dialog is named
    // by it. Escape, the backdrop and the button all close it.
    <Modal opened onClose={onClose} size="sm" centered withCloseButton={false} labelledBy={headingId} describedBy={bodyId}>
      <Stack gap="md" align="center" className="rr-topup-result">
        <div className={`rr-topup-result__icon rr-topup-result__icon--${phase}`} aria-hidden>
          {phase === 'success' ? <Success size={40} />
            : phase === 'failed' ? <ErrorIcon size={40} />
              : phase === 'slow' ? <Clock size={40} />
                : <Loader size="md" />}
        </div>

        {/* One live region, so a screen reader hears "Confirming" become the outcome. */}
        <Stack gap={4} align="center" role="status" aria-live="polite">
          <Text as="h2" id={headingId} role="headline" fw={600} ta="center">{heading}</Text>
          <Text id={bodyId} role="bodySmall" c="var(--rr-color-text-muted)" ta="center">{body}</Text>
        </Stack>

        {phase === 'success' && topup && (
          <dl className="rr-topup-result__facts">
            <div>
              <dt>{t('billing.topup.result.paid')}</dt>
              <dd>{formatMinor(topup.amount_minor, topup.currency)}</dd>
            </div>
            {!planPayment && (
              <>
                <div>
                  <dt>{t('billing.topup.result.added')}</dt>
                  <dd className="rr-topup-result__added">+{credits(topup.credited_minor ?? 0)}</dd>
                </div>
                <div>
                  <dt>{t('billing.topup.result.balance')}</dt>
                  <dd>{balanceFresh ? credits(walletQ.data!.data.balance_minor) : <Loader size="xs" />}</dd>
                </div>
              </>
            )}
          </dl>
        )}

        {topup && (
          <Text role="caption" c="var(--rr-color-text-muted)" ta="center">
            {phase === 'success' && <>{t('billing.topup.result.success.receipt')} </>}
            {t('billing.topup.result.reference', { reference: topup.provider_reference })}
          </Text>
        )}

        <Group justify="center" gap="sm">
          {phase === 'failed' && (
            <Button variant="default" onClick={() => { onClose(); nav('/billing/topup'); }}>
              {t('billing.topup.result.tryAgain')}
            </Button>
          )}
          <Button onClick={onClose}>{phase === 'success' ? t('billing.topup.result.done') : t('action.close')}</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
